import React from 'react'
import { ArrowRight, MessageCircle, Heart, BookOpen, Play, Sparkles } from 'lucide-react'

const CallToAction: React.FC = () => {
  return (
    <>
      {/* Community Support Section */}
      <section className="py-20 bg-gradient-to-br from-blue-900 via-purple-900 to-blue-900">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Patreon Support */}
            <div className="text-center md:text-left">
              <div className="flex items-center justify-center md:justify-start mb-4">
                <Heart className="w-8 h-8 text-red-400 mr-3 animate-pulse" />
                <h2 className="text-3xl sm:text-4xl font-bold text-white">
                  Support Our Vision
                </h2>
              </div>
              <p className="text-xl text-gray-300 mb-6 leading-relaxed">
                HammerMUD is free-to-play forever, powered by passionate community supporters who believe in the magic of text-based gaming
              </p>
              <div className="bg-gray-800/50 rounded-lg p-6 border border-gray-700 mb-6">
                <h3 className="text-lg font-semibold text-blue-400 mb-3">🚫 No Pay-to-Win Guarantee</h3>
                <ul className="text-gray-300 space-y-2 text-sm">
                  <li>• All content free for everyone</li>
                  <li>• Patreon supporters get recognition, never advantages</li>
                  <li>• Success based on skill, not wallet size</li>
                  <li>• Sustainable indie development model</li>
                </ul>
              </div>
              <a
                href="https://patreon.com/HammerMUD"
                className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-red-500 to-pink-500 text-white rounded-lg hover:from-red-600 hover:to-pink-600 transition-all duration-200 font-semibold text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Heart className="w-5 h-5 mr-2" />
                Support on Patreon
              </a>
            </div>

            {/* Latest Blog Highlight */}
            <div className="bg-gray-800/50 rounded-lg p-8 border border-gray-700">
              <div className="flex items-center mb-4">
                <BookOpen className="w-6 h-6 text-green-400 mr-3" />
                <h3 className="text-xl font-bold text-white">Latest from Our Blog</h3>
              </div>
              <h4 className="text-lg font-semibold text-blue-400 mb-3">
                Welcome to HammerMUD: A New Chapter in Text-Based Gaming
              </h4>
              <p className="text-gray-300 mb-4 leading-relaxed">
                We're building a modern MUD that bridges classic gameplay with contemporary convenience. 
                Free-to-play, community-driven, and designed for both veterans and newcomers.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href="/blog/welcome-to-hammermud"
                  className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200 font-medium"
                >
                  Read Full Post
                  <ArrowRight className="w-4 h-4 ml-2" />
                </a>
                <a
                  href="/blog"
                  className="inline-flex items-center px-4 py-2 border border-green-400 text-green-400 rounded-lg hover:bg-green-400 hover:text-white transition-colors duration-200 font-medium"
                >
                  View All Posts
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Call to Action */}
      <section className="py-20 bg-gradient-to-r from-gray-900 to-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="max-w-4xl mx-auto">
            {/* Main Headline */}
            <div className="flex items-center justify-center mb-6">
              <Sparkles className="w-8 h-8 text-blue-400 mr-3 animate-pulse" />
              <h2 className="text-3xl sm:text-4xl font-bold text-white">
                Your Adventure Awaits
              </h2>
              <Sparkles className="w-8 h-8 text-blue-400 ml-3 animate-pulse" />
            </div>
            
            {/* Subtitle */}
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              Join our growing community in the world of HammerMUD. Whether you're a seasoned 
              MUD veteran or new to text-based gaming, there's a place for you in our digital third space.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
              <a
                href="/app"
                className="inline-flex items-center px-8 py-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all duration-200 font-semibold text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-1"
              >
                <Play className="w-5 h-5 mr-2" />
                Start Your Journey
              </a>
              <a
                href="/about"
                className="inline-flex items-center px-8 py-4 border-2 border-blue-400 text-blue-400 rounded-lg hover:bg-blue-400 hover:text-white transition-all duration-200 font-semibold text-lg"
              >
                Learn Our Mission
                <ArrowRight className="w-5 h-5 ml-2" />
              </a>
            </div>

            {/* Community Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-12">
              <div className="bg-gray-800/50 rounded-lg p-6 border border-gray-700">
                <div className="text-2xl font-bold text-blue-400 mb-1">Free Forever</div>
                <div className="text-gray-300">Complete game access for everyone</div>
              </div>
              <div className="bg-gray-800/50 rounded-lg p-6 border border-gray-700">
                <div className="text-2xl font-bold text-green-400 mb-1">Community Driven</div>
                <div className="text-gray-300">Your feedback shapes development</div>
              </div>
              <div className="bg-gray-800/50 rounded-lg p-6 border border-gray-700">
                <div className="text-2xl font-bold text-purple-400 mb-1">Always Growing</div>
                <div className="text-gray-300">Regular content and feature updates</div>
              </div>
            </div>

            {/* Community Links */}
            <div className="border-t border-gray-700 pt-8">
              <p className="text-gray-400 mb-6 text-lg">
                Connect with our community
              </p>
              <div className="flex flex-col sm:flex-row justify-center items-center gap-6">
                <a
                  href="https://discord.gg/pSZtJccydN"
                  className="flex items-center space-x-3 text-gray-300 hover:text-white transition-colors duration-200 px-4 py-2 rounded-lg hover:bg-gray-800"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <MessageCircle className="w-6 h-6" />
                  <span className="font-medium">Join Discord Community</span>
                </a>
                <a
                  href="https://patreon.com/HammerMUD"
                  className="flex items-center space-x-3 text-red-300 hover:text-red-200 transition-colors duration-200 px-4 py-2 rounded-lg hover:bg-gray-800"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Heart className="w-6 h-6" />
                  <span className="font-medium">Support Development</span>
                </a>
                <a
                  href="/blog"
                  className="flex items-center space-x-3 text-green-300 hover:text-green-200 transition-colors duration-200 px-4 py-2 rounded-lg hover:bg-gray-800"
                >
                  <BookOpen className="w-6 h-6" />
                  <span className="font-medium">Read Development Blog</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}

export default CallToAction